import {
  Box,
  Flex,
  Text,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableCellProps,
} from '@chakra-ui/react';
import React, { useState } from 'react';

import {
  csvConversion,
  finleyAssert,
  finleyBuffer,
} from '@finley-technologies/common';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';

import { CSVPreview } from '../CSVPreview';
import { ExportBufferButton } from './ExportBufferButton';
import { useQAContext } from './QAContext';
import { IconButton } from 'packages/common-frontend/components/IconButton';
import { FiAperture, FiChevronUp } from 'react-icons/fi';

const commonTableCellStyleProps: TableCellProps = {
  py: '2',
};

type ExportNodeButtonProps = {
  tapeBuilderNode: TapeBuilderUI.TapeBuilderNodeAbstract;
};
export const ExportNodeButton = ({
  tapeBuilderNode,
}: ExportNodeButtonProps) => {
  finleyAssert(
    TapeBuilderUI.isTapeBuilderGeneratedTapeNode(tapeBuilderNode),
    `${tapeBuilderNode.metadata.tapeBuilderNodeType} is not a generated file node`,
  );

  const {
    getNodeBuffer,
    getNodeSupplementals,
    getNodeBufferExtension,
  } = useQAContext();
  const buffer = getNodeBuffer(tapeBuilderNode.metadata.tapeBuilderNodeId);
  const nodeBufferExtension = getNodeBufferExtension(
    tapeBuilderNode.metadata.tapeBuilderNodeId,
  );
  const supplementals = getNodeSupplementals(
    tapeBuilderNode.metadata.tapeBuilderNodeId,
  );

  const [showPreview, setShowPreview] = useState<boolean>(false);

  const filename = `${tapeBuilderNode.configuration.artifactSlug}.${nodeBufferExtension}`;
  const fileData = React.useMemo(() => {
    if (buffer === null) {
      return [];
    }

    if (nodeBufferExtension === 'json') {
      const json = JSON.parse(finleyBuffer.toString(buffer));
      const jsonStr = JSON.stringify(json, null, 2);
      return jsonStr;
    }
    const { data } = csvConversion.csvBufferToJsonArray({
      csvBuffer: buffer,
    });
    // just pick first 10 rows
    return data.slice(0, 10);
  }, [buffer, nodeBufferExtension]);

  const csvPreviewData =
    showPreview && nodeBufferExtension === 'csv' && (fileData as any[]);
  const jsonPreviewData =
    showPreview && nodeBufferExtension === 'json' && fileData;

  return (
    <Box id="export-node-button-container" p="2">
      <Text fontWeight="bold" mb="2">
        File Downloads
      </Text>

      <TableContainer>
        <Table w="fit-content">
          <Thead>
            <Tr>
              <Th {...commonTableCellStyleProps}>Action</Th>
              <Th {...commonTableCellStyleProps}>Filename</Th>
              <Th {...commonTableCellStyleProps}>Type</Th>
              <Th {...commonTableCellStyleProps}>Operation</Th>
              <Th {...commonTableCellStyleProps}>Notes</Th>
            </Tr>
          </Thead>
          <Tbody>
            {/* Main File Download */}
            <Tr>
              <Td {...commonTableCellStyleProps}>
                <Flex>
                  <ExportBufferButton filename={filename} buffer={buffer} />
                  <IconButton
                    isDisabled={!buffer}
                    ariaLabel={`${showPreview ? 'hide' : 'show'} file preview`}
                    label={`${showPreview ? 'Hide' : 'Show'} File Preview`}
                    icon={showPreview ? <FiChevronUp /> : <FiAperture />}
                    onClick={() => setShowPreview((curr) => !curr)}
                  />
                </Flex>
              </Td>
              <Td {...commonTableCellStyleProps}>{filename}</Td>
              <Td {...commonTableCellStyleProps}>Main</Td>
              <Td {...commonTableCellStyleProps}>--</Td>
              <Td {...commonTableCellStyleProps}>--</Td>
            </Tr>

            {/* Main File Preview */}
            {csvPreviewData && (
              <Tr>
                <Td colSpan={10}>
                  <CSVPreview data={csvPreviewData} />
                </Td>
              </Tr>
            )}

            {jsonPreviewData && (
              <Tr>
                <Td colSpan={10}>
                  <Box as="pre" overflowX="scroll" whiteSpace="pre-wrap">
                    {jsonPreviewData}
                  </Box>
                </Td>
              </Tr>
            )}

            {/* Supplemental File Downloads */}
            {supplementals.map(({ name, operation, outputBuffer, errors }) => (
              <Tr key={`${operation}-${name}`}>
                <Td {...commonTableCellStyleProps}>
                  <ExportBufferButton filename={name} buffer={outputBuffer} />
                </Td>
                <Td {...commonTableCellStyleProps}>{name}</Td>
                <Td {...commonTableCellStyleProps}>Supplemental</Td>
                <Td {...commonTableCellStyleProps}>{operation}</Td>
                <Td {...commonTableCellStyleProps}>
                  {errors.length > 0 ? errors.join(', ') : '--'}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
